import {useCallback, useEffect, useRef} from 'react';
import {SocketLikeWithNamespace} from 'socket.io-react-hook/lib/cjs/types';

import {SocketEvent} from 'enums';
import {logger} from 'utils';

import {useSocketEventCallback} from './socket-event-callback';

const LOG_PREFIX = '[Voice Reply]';
const withLogPrefix = (message: string) => `${LOG_PREFIX} ${message}`;
const DEFAULT_MIME_TYPE = 'audio/mpeg';

type VoiceReply = {
    // base64, not binary: a binary socket payload races the continuous ChunkHandled acks through socket.io's
    // decoder and drops the connection with a parse error. Decoded to bytes just before playback.
    audio: string;
    mimeType?: string;
    // Set on the final segment of a goodbye: when this one finishes playing, the conversation is over.
    endsSession?: boolean;
};

const base64ToBytes = (base64: string): Uint8Array => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
};

type Props = {
    socket: SocketLikeWithNamespace;
    // Playback belongs to a live voice-enabled activity: when it ends, nothing queued may keep talking.
    enabled: boolean;
    // Fired when a goodbye reply finishes playing on its own — not on barge-in, so a client who talks over it is
    // not treated as done. The caller reports it so the server can end the session right after.
    onConversationEnd?: () => void;
};

// The chatbot answers a spoken turn out loud. Replies arrive as finished audio, one per turn, so playing them is
// a plain <audio> job — they are queued rather than replacing each other, or a quick follow-up would cut off the
// answer the client is still listening to.
export function useVoiceReplyPlayback({socket, enabled, onConversationEnd}: Props) {
    const audioRef = useRef<HTMLAudioElement>(null);
    const objectUrlRef = useRef<string>(null);
    const queueRef = useRef<VoiceReply[]>([]);
    const onConversationEndRef = useRef(onConversationEnd);
    onConversationEndRef.current = onConversationEnd;

    const releaseCurrent = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.onended = null;
            audioRef.current.src = '';
            audioRef.current = null;
        }
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
    }, []);

    const stopAll = useCallback(() => {
        queueRef.current = [];
        releaseCurrent();
    }, [releaseCurrent]);

    const playNext = useCallback(() => {
        if (audioRef.current) {
            return;
        }
        const reply = queueRef.current.shift();
        if (!reply) {
            return;
        }

        try {
            const url = URL.createObjectURL(new Blob([base64ToBytes(reply.audio)], {type: reply.mimeType || DEFAULT_MIME_TYPE}));
            const element = new Audio(url);
            objectUrlRef.current = url;
            audioRef.current = element;

            element.onended = () => {
                releaseCurrent();
                // Natural finish only (barge-in nulls this handler in releaseCurrent), so talking over the
                // goodbye cancels the end instead of triggering it.
                if (reply.endsSession) {
                    onConversationEndRef.current?.();
                }
                playNext();
            };
            // Autoplay is allowed here: the client pressed the microphone, which is the interaction browsers
            // require. A rejection still must not pass unnoticed — silence would read as a dead bot.
            element.play().catch((error) => {
                logger.error(withLogPrefix('Unable to play the reply'), error);
                releaseCurrent();
                playNext();
            });
        } catch (error) {
            logger.error(withLogPrefix('Unable to prepare the reply'), error);
            releaseCurrent();
        }
    }, [releaseCurrent]);

    useSocketEventCallback(socket, SocketEvent.ActiveSessionActivityVoiceReply, useCallback((reply: VoiceReply) => {
        if (!enabled || !reply?.audio) {
            return;
        }
        queueRef.current = [...queueRef.current, reply];
        playNext();
    }, [enabled, playNext]));

    useEffect(() => {
        if (!enabled) {
            stopAll();
        }
    }, [enabled, stopAll]);

    // The widget can unmount mid-reply — the audio element would otherwise keep playing detached from the page.
    useEffect(() => stopAll, [stopAll]);

    // Exposed for barge-in: the client speaking over the bot has to silence it, queue included.
    return {stop: stopAll};
}
